import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, List, ListItem, ListItemText, Checkbox, IconButton, TextField, Alert, Button, Menu, MenuItem, Popover, FormControl, Select, Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PanToolIcon from '@mui/icons-material/PanTool';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import { caiValues } from '../constants/caiValues';

const mandatoryItems = [
  { id: 'context', title: 'Context', color: '#FFD700' },
  { id: 'title', title: 'Title', color: '#A3CEF1' },
  { id: 'url', title: 'URL', color: '#F9D5A7' },
  { id: 'content', title: 'Content', color: '#B6E2D3' },
  { id: 'pubdate', title: 'Publication Date', color: '#E2C2F0' },
];

const optionalItems = [
  { id: 'pagination', title: 'Pagination', color: '#F7B7A3' },
  { id: 'summary', title: 'Summary', color: '#F6A6B2' },
  { id: 'citation', title: 'Citation', color: '#C2F0FC' },
  { id: 'effective_date', title: 'Effective Date', color: '#B0E57C' }, // New field
  { id: 'category', title: 'Doc Type', color: '#FFB74D' }, // New field
];

const Inspector = ({
  selectedItems,
  selectedElements,
  activeInspectorItem,
  editingPath,
  editingValue,
  editingError,
  aiMagicLoading,
  includeIngestion,
  videos,
  loadingVideos,
  onSelectItem,
  onInspectorSelect,
  onInspectorAIMatch,
  onInspectorAIMatchItem,
  onIncludeIngestionChange,
  onEditingPathChange,
  onEditingValueChange,
  onEditingPathSave,
  onEditingPathCancel,
  getInspectorColor,
  setActiveInspectorItem,
  pubDateIncomplete,
  onPubDateIncompleteChange,
  updateInspectorSelection,
  htmlContent,
  onContentTypeChange,
  contentType
}) => {
  console.log('[Inspector] Rendered');
  const allItems = [...mandatoryItems, ...optionalItems];
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  const [regexEditing, setRegexEditing] = useState({});
  const [regexValue, setRegexValue] = useState({});
  const [regexAILoading, setRegexAILoading] = useState({});
  const [conversionMapInputs, setConversionMapInputs] = useState({});

  const handleMenuClick = (event, itemId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveItemId(itemId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };

  const handleEditClick = () => {
    if (activeItemId) {
      onEditingPathChange(activeItemId, true);
    }
    handleMenuClose();
  };

  const handleAIFixClick = () => {
    if (activeItemId) {
      onInspectorAIMatchItem(activeItemId);
    }
    handleMenuClose();
  };

  const handleRegexClick = () => {
    if (activeItemId) {
      setRegexEditing(prev => ({ ...prev, [activeItemId]: true }));
      setRegexValue(prev => ({ ...prev, [activeItemId]: '' }));
    }
    handleMenuClose();
  };

  const handleRegexSave = (itemId) => {
    const regex = regexValue[itemId];
    if (regex && regex.trim()) {
      // Store the regex value in the selectedElements with a special key
      const regexKey = `${itemId}_regex`;
      updateInspectorSelection(regexKey, regex);
      console.log('Saving regex for item:', itemId, 'value:', regex);
    }
    setRegexEditing(prev => ({ ...prev, [itemId]: false }));
  };

  const handleRegexCancel = (itemId) => {
    setRegexEditing(prev => ({ ...prev, [itemId]: false }));
    setRegexValue(prev => ({ ...prev, [itemId]: '' }));
  };

  const handleConversionMapInputChange = (itemId, field, value) => {
    setConversionMapInputs(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const handleAddConversionMap = (itemId) => {
    const input = conversionMapInputs[itemId];
    if (input?.cai && input?.source) {
      const currentMap = selectedElements[`${itemId}_conversion_map`] || {};
      const newMap = {
        ...currentMap,
        [input.source]: input.cai
      };
      
      // Update the selectedElements with the new conversion map
      const updatedElements = {
        ...selectedElements,
        [`${itemId}_conversion_map`]: newMap
      };
      
      // Call the update function passed from parent
      updateInspectorSelection(`${itemId}_conversion_map`, newMap);
      
      // Clear the input fields
      setConversionMapInputs(prev => ({
        ...prev,
        [itemId]: { cai: '', source: '' }
      }));
    }
  };

  const handleRemoveConversionMap = (itemId, source) => {
    const currentMap = selectedElements[`${itemId}_conversion_map`] || {};
    const newMap = { ...currentMap };
    delete newMap[source];
    
    // Update the selectedElements with the updated conversion map
    updateInspectorSelection(`${itemId}_conversion_map`, Object.keys(newMap).length > 0 ? newMap : null);
  };

  const handleRegexAI = async (itemId) => {
    const instructions = regexValue[itemId];
    if (!instructions || instructions.trim() === '') {
      return;
    }

    setRegexAILoading(prev => ({ ...prev, [itemId]: true }));

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: 'You are a regex expert. Write regex patterns based on user instructions. Always respond in JSON format with a "regex" key containing the regex pattern. IMPORTANT: When writing regex patterns in JSON, you must escape all backslashes by doubling them (e.g., \\w instead of \\w, \\s instead of \\s, \\d instead of \\d, etc.).'
            },
            {
              role: 'user',
              content: `Write a regex based on the following text and respond in json format {regex: value}.

Instructions: ${instructions}`
            }
          ],
          temperature: 0.3
        })
      });

      if (response.ok) {
        const data = await response.json();
        const content = data.choices[0]?.message?.content;
        
        if (content) {
          try {
            // Try to parse as JSON first
            console.log('Attempting to parse JSON response:', content);
            const jsonResponse = JSON.parse(content);
            if (jsonResponse.regex) {
              setRegexValue(prev => ({ ...prev, [itemId]: jsonResponse.regex }));
            } else {
              console.error('No regex field in AI response:', content);
            }
          } catch (error) {
            console.log('JSON parsing failed, trying fallback regex parsing. Error:', error.message);
            // Fallback to regex matching if JSON parsing fails
            // Try to match both JSON format {"regex": "value"} and legacy format {regex: "value"}
            // Use a more robust pattern that can handle escaped characters
            console.log('Trying fallback regex parsing on content:', content);
            const jsonMatch = content.match(/\{regex:\s*"((?:[^"\\]|\\.)*)"\}/) || content.match(/\{"regex":\s*"((?:[^"\\]|\\.)*)"\}/);
            console.log('Fallback regex match result:', jsonMatch);
            if (jsonMatch) {
              let generatedRegex = jsonMatch[1];
              console.log('Extracted regex before escaping:', generatedRegex);
              // If the regex contains unescaped backslashes, escape them properly
              generatedRegex = generatedRegex.replace(/\\([wsdDnrtbfv])/g, '\\\\$1');
              console.log('Extracted regex after escaping:', generatedRegex);
              setRegexValue(prev => ({ ...prev, [itemId]: generatedRegex }));
            } else {
              // Try manual parsing for malformed JSON with unescaped backslashes
              console.log('Trying manual parsing for malformed JSON');
              const manualMatch = content.match(/"regex":\s*"([^"]+)"/);
              if (manualMatch) {
                let generatedRegex = manualMatch[1];
                console.log('Manually extracted regex before escaping:', generatedRegex);
                // Escape unescaped backslashes
                generatedRegex = generatedRegex.replace(/\\([wsdDnrtbfv])/g, '\\\\$1');
                console.log('Manually extracted regex after escaping:', generatedRegex);
                setRegexValue(prev => ({ ...prev, [itemId]: generatedRegex }));
              } else {
                console.error('Could not parse regex from AI response:', content);
              }
            }
          }
        }
      } else {
        console.error('OpenAI API error:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error calling OpenAI:', error);
    } finally {
      setRegexAILoading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Function to apply regex to content and get result
  const getRegexResult = (itemId) => {
    const selectorPath = selectedElements[itemId];
    const regexPattern = selectedElements[`${itemId}_regex`];
    
    if (!selectorPath || !regexPattern || !htmlContent) {
      return null;
    }

    try {
      // Create a temporary DOM element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Find the first element matching the selector
      const elements = tempDiv.querySelectorAll(selectorPath);
      if (elements.length === 0) {
        return 'No elements found with selector';
      }
      
      const firstElement = elements[0];
      const elementText = firstElement.textContent || firstElement.innerText || '';
      
      // Parse regex flags from the pattern
      let pattern = regexPattern;
      let flags = 'g';
      
      // Handle common regex flag patterns
      if (pattern.startsWith('(?i)')) {
        flags = 'gi';
        pattern = pattern.substring(4);
      } else if (pattern.startsWith('(?s)')) {
        flags = 'gs';
        pattern = pattern.substring(4);
      } else if (pattern.startsWith('(?m)')) {
        flags = 'gm';
        pattern = pattern.substring(4);
      } else if (pattern.startsWith('(?u)')) {
        flags = 'gu';
        pattern = pattern.substring(4);
      }
      
      // Apply the regex
      const regex = new RegExp(pattern, flags);
      console.log('Applying regex:', pattern, 'with flags:', flags, 'to text:', elementText.substring(0, 100) + '...');
      const matches = elementText.match(regex);
      
      if (!matches) {
        return 'No regex matches found';
      }
      
      console.log('Found matches:', matches);
      return matches.join(', ');
    } catch (error) {
      return `Error: ${error.message}`;
    }
  };

  return (
    <Card 
      elevation={0}
      sx={{ 
        borderRadius: 3, 
        minHeight: 800, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)'
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {activeInspectorItem && (
          <Box sx={{ 
            bgcolor: '#d32f2f', 
            color: 'white', 
            p: 2, 
            borderRadius: 2, 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="body2" fontWeight={600}>
              Inspecting: {allItems.find(item => item.id === activeInspectorItem)?.title}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setActiveInspectorItem(null)}
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>Element Inspector</Typography>
          <IconButton aria-label="AI Match" onClick={onInspectorAIMatch} sx={{ color: '#1976d2', ml: 1 }} disabled={aiMagicLoading}>
            <AutoFixHighIcon />
          </IconButton>
          {aiMagicLoading && (
            <Typography variant="body2" sx={{ color: '#d32f2f', ml: 2 }}>
              Doing some magic...
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 600 }}>Mandatory Items</Typography>
        <List dense sx={{ mb: 2 }}>
          {mandatoryItems.map((item) => {
            const isSelected = item.id === 'pubdate'
              ? (selectedItems.includes(item.id) || pubDateIncomplete)
              : selectedItems.includes(item.id);
            const isInspecting = activeInspectorItem === item.id;
            const selectorPath = selectedElements[item.id];
            const contextSelected = selectedElements['context'] && selectedElements['context'].trim();
            // Only disable title, url, pubdate, summary, citation when context is not selected
            const itemsRequiringContext = ['title', 'url', 'pubdate', 'summary', 'citation'];
            const shouldBeDisabled = itemsRequiringContext.includes(item.id) && !contextSelected;
            
            return (
              <ListItem
                key={item.id}
                sx={{
                  border: `2px solid ${getInspectorColor(item.id)}`,
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: shouldBeDisabled ? '#f5f5f5' : `${getInspectorColor(item.id)}10`,
                  cursor: shouldBeDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: isInspecting ? `0 0 0 2px #1677ff` : undefined,
                  position: 'relative',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  opacity: shouldBeDisabled ? 0.6 : 1,
                  '&:hover': {
                    ...(shouldBeDisabled ? {} : {
                      borderColor: getInspectorColor(item.id),
                      bgcolor: `${getInspectorColor(item.id)}20`
                    })
                  }
                }}
                onClick={(e) => {
                  if (shouldBeDisabled) return;
                  e.stopPropagation();
                  onSelectItem(item.id);
                }}
              >
                {/* Row 1: Title and Select Button */}
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="body1" fontWeight={600} sx={{ color: shouldBeDisabled ? '#999' : 'inherit' }}>
                    {item.title}
                    {shouldBeDisabled && <Typography component="span" variant="caption" sx={{ ml: 1, color: '#999' }}>
                      (Context required)
                    </Typography>}
                  </Typography>
                  <Button
                    size="small"
                    variant={isInspecting || isSelected ? "contained" : "outlined"}
                    onClick={e => { 
                      if (shouldBeDisabled || (item.id === 'pubdate' && pubDateIncomplete)) return;
                      e.stopPropagation(); 
                      onInspectorSelect(item.id);
                    }}
                    disabled={shouldBeDisabled || (item.id === 'pubdate' && pubDateIncomplete)}
                    sx={{ 
                      color: isInspecting || selectorPath ? 'white' : '#666',
                      bgcolor: isInspecting ? '#1677ff' : selectorPath ? '#000' : 'white',
                      borderColor: isInspecting ? '#1677ff' : selectorPath ? '#000' : getInspectorColor(item.id),
                      '&:hover': {
                        bgcolor: isInspecting ? '#1677ff' : selectorPath ? '#333' : `${getInspectorColor(item.id)}10`
                      },
                      textTransform: 'none',
                      minWidth: 80
                    }}
                  >
                    {isInspecting ? 'Inspecting' : selectorPath ? 'Selected' : 'Select'}
                  </Button>
                </Box>
                {/* PubDate Checkbox on next row */}
                {item.id === 'pubdate' && (
                  <Box display="flex" alignItems="center" p={0} mt={-1}>
                    <Checkbox
                      checked={!!pubDateIncomplete}
                      onChange={e => {
                        e.stopPropagation();
                        onPubDateIncompleteChange(e.target.checked);
                        if (e.target.checked) {
                          // Mark as selected if not already
                          if (!selectedItems.includes(item.id)) onSelectItem(item.id);
                        } else {
                          // If unchecking and no path is set, remove from selected items
                          if (!selectedElements[item.id]) {
                            onSelectItem(item.id);
                          }
                        }
                      }}
                      disabled={shouldBeDisabled}
                      sx={{ pl: 0}}
                    />
                    <Typography variant="body2" sx={{ color: shouldBeDisabled ? '#999' : 'inherit', ml: 0 }}>
                    Allow incomplete
                    </Typography>
                  </Box>
                )}

                {/* Content Type Select on next row */}
                {item.id === 'content' && selectorPath && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="body2" sx={{ color: '#666', minWidth: 40 }}>
                      Type:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={contentType}
                        onChange={(e) => {
                          if (onContentTypeChange) {
                            onContentTypeChange(e.target.value);
                          }
                        }}
                        sx={{ 
                          height: 32,
                          '& .MuiSelect-select': { py: 0.5 }
                        }}
                      >
                        <MenuItem value="HTML">HTML</MenuItem>
                        <MenuItem value="PDF">PDF</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Row 2: Path and Editing Options */}
                {selectorPath && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: getInspectorColor(item.id), fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      {selectorPath}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleMenuClick(e, item.id)}
                      sx={{ 
                        color: '#666', 
                        flexShrink: 0,
                        borderColor: '#ddd',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        '&:hover': {
                          borderColor: '#999',
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                      disabled={aiMagicLoading}
                    >
                      Options
                    </Button>
                  </Box>
                )}

                {/* Row 3: Regex Value */}
                {selectedElements[`${item.id}_regex`] && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      Regex: {selectedElements[`${item.id}_regex`]}
                    </Typography>
                  </Box>
                )}

                {/* Row 4: Regex Result */}
                {selectedElements[`${item.id}_regex`] && selectedElements[item.id] && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      Regex result: {getRegexResult(item.id)}
                    </Typography>
                  </Box>
                )}

                {/* Modern Popover Menu */}
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -6,
                        right: 20,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: '6px solid white',
                        filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))'
                      }
                    }
                  }}
                >
                  <Box sx={{ py: 1 }}>
                    <MenuItem 
                      onClick={handleEditClick}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <Typography variant="body2">Edit</Typography>
                    </MenuItem>
                    <MenuItem 
                      onClick={handleAIFixClick} 
                      disabled={aiMagicLoading}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&.Mui-disabled': { opacity: 0.5 }
                      }}
                    >
                      <Typography variant="body2">AI Fix</Typography>
                    </MenuItem>
                    <MenuItem 
                      onClick={handleRegexClick}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <Typography variant="body2">Regex</Typography>
                    </MenuItem>
                  </Box>
                </Popover>

                {/* Editing Section */}
                {editingPath[item.id] && (
                  <Box sx={{ 
                    mt: 2, 
                    width: '100%', 
                    borderTop: '1px solid #e0e0e0', 
                    pt: 2 
                  }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={4}
                      value={editingValue[item.id] || selectorPath || ''}
                      onChange={(e) => onEditingValueChange(item.id, e.target.value)}
                      placeholder="Enter CSS selector..."
                      sx={{ mb: 1 }}
                    />
                    {editingError[item.id] && (
                      <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                        <ErrorOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        {editingError[item.id]}
                      </Alert>
                    )}
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onEditingPathSave(item.id)}
                        sx={{ 
                          bgcolor: '#1976d2',
                          color: 'white',
                          '&:hover': { bgcolor: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onEditingPathCancel(item.id)}
                        sx={{ 
                          borderColor: '#666',
                          color: '#666',
                          '&:hover': { borderColor: '#333', color: '#333' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Regex Section */}
                {regexEditing[item.id] && (
                  <Box sx={{ 
                    mt: 2, 
                    width: '100%', 
                    borderTop: '1px solid #e0e0e0', 
                    pt: 2 
                  }}>
                    <Box display="flex" gap={1} sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={regexValue[item.id] || ''}
                        onChange={(e) => setRegexValue(prev => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Enter pattern or describe for AI"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRegexAI(item.id)}
                        disabled={!regexValue[item.id] || regexValue[item.id].trim() === '' || regexAILoading[item.id]}
                        sx={{ 
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          '&:hover': { borderColor: '#1565c0', color: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 40,
                          px: 2
                        }}
                      >
                        {regexAILoading[item.id] ? '...' : 'AI'}
                      </Button>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleRegexSave(item.id)}
                        sx={{ 
                          bgcolor: '#1976d2',
                          color: 'white',
                          '&:hover': { bgcolor: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRegexCancel(item.id)}
                        sx={{ 
                          borderColor: '#666',
                          color: '#666',
                          '&:hover': { borderColor: '#333', color: '#333' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>

        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 600 }}>Optional Items</Typography>
        <List dense sx={{ mb: 2 }}>
          {optionalItems.map((item) => {
            const isSelected = selectedItems.includes(item.id);
            const isInspecting = activeInspectorItem === item.id;
            const selectorPath = selectedElements[item.id];
            const contextSelected = selectedElements['context'] && selectedElements['context'].trim();
            // Only summary, citation, effective_date, and category require context
            const contextRequiredOptional = ['summary', 'citation', 'effective_date', 'category'];
            const shouldBeDisabled = contextRequiredOptional.includes(item.id) && !contextSelected;
            
            return (
              <ListItem
                key={item.id}
                sx={{
                  border: `2px solid ${getInspectorColor(item.id)}`,
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: shouldBeDisabled ? '#f5f5f5' : `${getInspectorColor(item.id)}10`,
                  cursor: shouldBeDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: isInspecting ? `0 0 0 2px #1677ff` : undefined,
                  position: 'relative',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  opacity: shouldBeDisabled ? 0.6 : 1,
                  '&:hover': {
                    ...(shouldBeDisabled ? {} : {
                      borderColor: getInspectorColor(item.id),
                      bgcolor: `${getInspectorColor(item.id)}20`
                    })
                  }
                }}
                onClick={(e) => {
                  if (shouldBeDisabled) return;
                  e.stopPropagation();
                  onSelectItem(item.id);
                }}
              >
                {/* Row 1: Title and Select Button */}
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="body1" fontWeight={600} sx={{ color: shouldBeDisabled ? '#999' : 'inherit' }}>
                    {item.title}
                    {shouldBeDisabled && <Typography component="span" variant="caption" sx={{ ml: 1, color: '#999' }}>
                      (Context required)
                    </Typography>}
                  </Typography>
                  <Button
                    size="small"
                    variant={isInspecting || selectorPath ? "contained" : "outlined"}
                    onClick={e => { 
                      if (shouldBeDisabled) return;
                      e.stopPropagation(); 
                      onInspectorSelect(item.id);
                    }}
                    disabled={shouldBeDisabled}
                    sx={{ 
                      color: isInspecting || selectorPath ? 'white' : '#666',
                      bgcolor: isInspecting ? '#1677ff' : selectorPath ? '#000' : 'white',
                      borderColor: isInspecting ? '#1677ff' : selectorPath ? '#000' : getInspectorColor(item.id),
                      '&:hover': {
                        bgcolor: isInspecting ? '#1677ff' : selectorPath ? '#333' : `${getInspectorColor(item.id)}10`
                      },
                      textTransform: 'none',
                      minWidth: 80
                    }}
                  >
                    {isInspecting ? 'Inspecting' : selectorPath ? 'Selected' : 'Select'}
                  </Button>
                </Box>

                {/* Row 2: Path and Editing Options */}
                {selectorPath && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: getInspectorColor(item.id), fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      {selectorPath}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleMenuClick(e, item.id)}
                      sx={{ 
                        color: '#666', 
                        flexShrink: 0,
                        borderColor: '#ddd',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        '&:hover': {
                          borderColor: '#999',
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                      disabled={aiMagicLoading}
                    >
                      Options
                    </Button>
                  </Box>
                )}

                {/* Row 3: Regex Value */}
                {selectedElements[`${item.id}_regex`] && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      Regex: {selectedElements[`${item.id}_regex`]}
                    </Typography>
                  </Box>
                )}

                {/* Row 4: Regex Result */}
                {selectedElements[`${item.id}_regex`] && selectedElements[item.id] && !editingPath[item.id] && (
                  <Box display="flex" alignItems="center" gap={1} mt={1} width="100%">
                    <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}>
                      Regex result: {getRegexResult(item.id)}
                    </Typography>
                  </Box>
                )}

                {/* Row 5: Conversion Map for Category */}
                {item.id === 'category' && selectedElements[item.id] && !editingPath[item.id] && (
                  <Box sx={{ mt: 1, width: '100%' }}>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 1 }}>
                      Conversion Mapping:
                    </Typography>
                    
                    {/* Display existing conversion map */}
                    {selectedElements[`${item.id}_conversion_map`] && Object.keys(selectedElements[`${item.id}_conversion_map`]).length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {Object.entries(selectedElements[`${item.id}_conversion_map`]).map(([source, cai], index) => (
                          <Box 
                            key={source} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              p: 1, 
                              bgcolor: '#f5f5f5', 
                              borderRadius: 1, 
                              mb: 0.5 
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#666', minWidth: 60 }}>
                              {source} →
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', flexGrow: 1 }}>
                              {cai}{index === 0 ? ' (Default)' : ''}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveConversionMap(item.id, source)}
                              sx={{ color: '#d32f2f', p: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Add new conversion mapping */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Source Value"
                        value={conversionMapInputs[item.id]?.source || ''}
                        onChange={(e) => handleConversionMapInputChange(item.id, 'source', e.target.value)}
                        sx={{ 
                          flex: 1, 
                          '& .MuiInputBase-root': { height: 32 },
                          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
                        }}
                      />
                      <Autocomplete
                        size="small"
                        options={caiValues}
                        value={conversionMapInputs[item.id]?.cai || ''}
                        onChange={(event, newValue) => handleConversionMapInputChange(item.id, 'cai', newValue || '')}
                        onInputChange={(event, newInputValue) => handleConversionMapInputChange(item.id, 'cai', newInputValue)}
                        freeSolo
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="CAI Value"
                            sx={{ 
                              '& .MuiInputBase-root': { height: 32 },
                              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' }
                            }}
                          />
                        )}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddConversionMap(item.id)}
                        disabled={!conversionMapInputs[item.id]?.cai || !conversionMapInputs[item.id]?.source}
                        sx={{ 
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          '&:hover': { borderColor: '#1565c0', color: '#1565c0' },
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          py: 0.5,
                          px: 1.5,
                          minWidth: 60,
                          height: 32
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Modern Popover Menu */}
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      borderRadius: 2,
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -6,
                        right: 20,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderBottom: '6px solid white',
                        filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.1))'
                      }
                    }
                  }}
                >
                  <Box sx={{ py: 1 }}>
                    <MenuItem 
                      onClick={handleEditClick}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <Typography variant="body2">Edit</Typography>
                    </MenuItem>
                    <MenuItem 
                      onClick={handleAIFixClick} 
                      disabled={aiMagicLoading}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&.Mui-disabled': { opacity: 0.5 }
                      }}
                    >
                      <Typography variant="body2">AI Fix</Typography>
                    </MenuItem>
                    <MenuItem 
                      onClick={handleRegexClick}
                      sx={{ 
                        px: 2, 
                        py: 1.5,
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}
                    >
                      <Typography variant="body2">Regex</Typography>
                    </MenuItem>
                  </Box>
                </Popover>

                {/* Editing Section */}
                {editingPath[item.id] && (
                  <Box sx={{ 
                    mt: 2, 
                    width: '100%', 
                    borderTop: '1px solid #e0e0e0', 
                    pt: 2 
                  }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={4}
                      value={editingValue[item.id] || selectorPath || ''}
                      onChange={(e) => onEditingValueChange(item.id, e.target.value)}
                      placeholder="Enter CSS selector..."
                      sx={{ mb: 1 }}
                    />
                    {editingError[item.id] && (
                      <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                        <ErrorOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        {editingError[item.id]}
                      </Alert>
                    )}
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onEditingPathSave(item.id)}
                        sx={{ 
                          bgcolor: '#1976d2',
                          color: 'white',
                          '&:hover': { bgcolor: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onEditingPathCancel(item.id)}
                        sx={{ 
                          borderColor: '#666',
                          color: '#666',
                          '&:hover': { borderColor: '#333', color: '#333' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Regex Section */}
                {regexEditing[item.id] && (
                  <Box sx={{ 
                    mt: 2, 
                    width: '100%', 
                    borderTop: '1px solid #e0e0e0', 
                    pt: 2 
                  }}>
                    <Box display="flex" gap={1} sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={regexValue[item.id] || ''}
                        onChange={(e) => setRegexValue(prev => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Enter pattern or describe for AI"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRegexAI(item.id)}
                        disabled={!regexValue[item.id] || regexValue[item.id].trim() === '' || regexAILoading[item.id]}
                        sx={{ 
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          '&:hover': { borderColor: '#1565c0', color: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 40,
                          px: 2
                        }}
                      >
                        {regexAILoading[item.id] ? '...' : 'AI'}
                      </Button>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleRegexSave(item.id)}
                        sx={{ 
                          bgcolor: '#1976d2',
                          color: 'white',
                          '&:hover': { bgcolor: '#1565c0' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRegexCancel(item.id)}
                        sx={{ 
                          borderColor: '#666',
                          color: '#666',
                          '&:hover': { borderColor: '#333', color: '#333' },
                          textTransform: 'none',
                          minWidth: 60
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>

        {/* Videos Section */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontWeight: 600 }}>Recorded Videos</Typography>
        {loadingVideos ? (
          <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 2 }}>
            Loading videos...
          </Typography>
        ) : videos.length > 0 ? (
          <List dense sx={{ mb: 2 }}>
            {videos.map((video, index) => (
              <ListItem
                key={index}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: '#f8f9fa',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {(() => {
                      const fileName = video.name.split('/').pop().replace('.webm', '');
                      // Remove timestamp from the end (format: path-timestamp)
                      const parts = fileName.split('-');
                      const timestamp = parts[parts.length - 1];
                      if (!isNaN(timestamp)) {
                        return parts.slice(0, -1).join('-');
                      }
                      return fileName;
                    })()}
                  </Typography>
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => window.open(video.url, '_blank')}
                      sx={{ color: '#1976d2' }}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = video.url;
                        link.download = video.name.split('/').pop();
                        link.click();
                      }}
                      sx={{ color: '#666' }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                {/* Show the page_url for clarity */}
                {video.page_url && (
                  <Typography variant="caption" sx={{ color: '#888', mt: 0.5, wordBreak: 'break-all' }}>
                    Page: {video.page_url}
                  </Typography>
                )}
                {video.created_at && (
                  <Typography variant="caption" sx={{ color: '#888', mt: 0.5 }}>
                    Recorded: {new Date(video.created_at).toLocaleString()}
                  </Typography>
                )}
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 2 }}>
            No videos recorded for this URL yet.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default Inspector; 